import React, { useState } from "react";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
} from "date-fns";
import "../App.css";

const Scheduler = ({ eventData }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [overlayData, setOverlayData] = useState({
    visible: false,
    day: null,
    events: [],
  });

  const renderHeader = () => {
    return (
      <div className="header flex justify-between border-b p-2">
        <span className="text-lg font-bold">
          {format(currentMonth, "MMMM yyyy")}
        </span>
        <div className="buttons">
          <button
            className="p-1"
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
          >
            <svg
              width="1em"
              height="1em"
              viewBox="0 0 16 16"
              className="bi bi-arrow-left-circle"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"
              />
              <path
                fillRule="evenodd"
                d="M8.354 11.354a.5.5 0 0 0 0-.708L5.707 8l2.647-2.646a.5.5 0 1 0-.708-.708l-3 3a.5.5 0 0 0 0 .708l3 3a.5.5 0 0 0 .708 0z"
              />
              <path
                fillRule="evenodd"
                d="M11.5 8a.5.5 0 0 0-.5-.5H6a.5.5 0 0 0 0 1h5a.5.5 0 0 0 .5-.5z"
              />
            </svg>
          </button>
          <button
            className="p-1"
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          >
            <svg
              width="1em"
              height="1em"
              viewBox="0 0 16 16"
              className="bi bi-arrow-right-circle"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"
              />
              <path
                fillRule="evenodd"
                d="M7.646 11.354a.5.5 0 0 1 0-.708L10.293 8 7.646 5.354a.5.5 0 1 1 .708-.708l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0z"
              />
              <path
                fillRule="evenodd"
                d="M4.5 8a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5z"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  const isToday = (date) => {
    const today = new Date();
    return isSameDay(date, today);
  };

  const renderDays = () => {
    const days = [];
    const startDate = startOfWeek(startOfMonth(currentMonth));
    const endDate = endOfWeek(endOfMonth(currentMonth));

    let day = startDate;
    while (day <= endDate) {
      // Filter events that match the current day
      const eventsForDay = eventData.filter((event) => {
        // Adjust month to zero-indexed before comparing with the day
        const eventDate = new Date(event.Tanggal);
        const eventMonth = eventDate.getMonth() + 1; // Get zero-indexed month
        const eventDay = eventDate.getDate();
        const eventYear = eventDate.getFullYear();
        const currentDay = new Date(day);

        return (
          currentDay.getFullYear() === eventYear &&
          currentDay.getMonth() === eventMonth && // Ensure month matches after adjustment
          currentDay.getDate() === eventDay
        );
      });

      days.push(
        <td
          className={`border border-black p-1 h-40 xl:w-40 lg:w-30 md:w-30 sm:w-20 w-10 overflow-auto transition cursor-pointer duration-500 ease hover:bg-gray-300`}
          key={day}
          onClick={() => toggleOverlay(day, eventsForDay)} // Add click handler to toggle overlay
        >
          <div className="flex flex-col h-40 mx-auto xl:w-40 lg:w-30 md:w-30 sm:w-full w-10 mx-auto overflow-hidden">
            <div className="top h-5 w-full">
              <span
                className={`text-sm ${
                  isToday(day) ? "font-bold text-indigo-500" : "text-gray-500"
                }`}
              >
                {format(day, "d")} {isToday(day) && <span>(today)</span>}
              </span>
            </div>

            <div className="bottom flex-grow h-30 py-1 w-full cursor-pointer">
              {eventsForDay.map((event, index) => (
                <div key={index} className="text-xs mt-2 flex items-center">
                  <span className="w-1 h-4 bg-indigo-500 mx-2"></span>
                  {overlayData.visible && isSameDay(day, overlayData.day) ? (
                    <a
                      href={event.Link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {event["Nama Acara"]}
                    </a>
                  ) : (
                    <span>{event["Nama Acara"]}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </td>
      );
      day = addDays(day, 1);
    }

    const toggleOverlay = (day, events) => {
      // Convert day to the same format as overlayData.day
      const formattedDay = format(day, "yyyy-MM-dd");

      if (
        overlayData.visible &&
        format(overlayData.day, "yyyy-MM-dd") === formattedDay
      ) {
        setOverlayData({ visible: false, day: null, events: [] });
      } else {
        setOverlayData({ visible: true, day: day, events: events });
      }
    };

    const rows = [];
    let cells = [];

    days.forEach((day, i) => {
      if (i % 7 !== 0) {
        cells.push(day);
      } else {
        rows.push(cells);
        cells = [];
        cells.push(day);
      }
      if (i === days.length - 1) {
        rows.push(cells);
      }
    });

    // Add empty cells to the last row if needed to make it 7 cells wide
    const lastRow = rows[rows.length - 1];
    if (lastRow.length < 7) {
      const emptyCells = Array(7 - lastRow.length).fill(
        <td
          key={`empty-${lastRow.length}`}
          className={`border p-1 h-40 xl:w-40 lg:w-30 md:w-30 sm:w-20 w-10`}
        ></td>
      );
      rows[rows.length - 1] = [...lastRow, ...emptyCells];
    }

    return (
      <>
        {rows.map((row, i) => (
          <tr key={i}>{row}</tr>
        ))}
        {overlayData.visible && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-4 rounded-lg shadow-lg w-11/12 max-w-lg">
              <h2 className="text-xl mb-4 h-full">Events on this day</h2>
              {overlayData.events.length > 0 ? (
                overlayData.events.map((event, index) => (
                  <div key={index} className="text-sm mb-2 flex items-center">
                    <span className="w-1 h-4 bg-indigo-500 mx-2 transition-colors duration-300 hover:bg-indigo-700"></span>
                    <a
                      href={event.Link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="event-link transition-colors duration-300 hover:text-indigo-700"
                    >
                      {event["Nama Acara"]}
                    </a>
                  </div>
                ))
              ) : (
                <p>No events for this day.</p>
              )}

              <button
                className="mt-4 py-2 px-4 bg-blue-500 text-white rounded"
                onClick={() =>
                  setOverlayData({ visible: false, day: null, events: [] })
                }
              >
                Close
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderWeekDays = () => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return (
      <tr className="weekdays">
        {days.map((day, i) => (
          <th
            key={i}
            className="border border-black p-2 border-r h-10 xl:w-40 lg:w-30 md:w-30 sm:w-20 w-10 xl:text-sm text-xs bg-slate-400 text-white"
          >
            {day}
          </th>
        ))}
      </tr>
    );
  };

  return (
    <div className="mx-auto container mt-10 h-full">
      <div>Calendar Of Event</div>
      <div className="bg-white rounded shadow w-full">
        {renderHeader()}
        <table className="w-full">
          <thead>{renderWeekDays()}</thead>
          <tbody>{renderDays()}</tbody>
        </table>
      </div>
    </div>
  );
};

export default Scheduler;
